import React, { useEffect } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { checkMediaSize } from '../../../../utils/helpers';
import { CompanyMediaModal } from './CompanyMediaModal';
import { ImagePlaceholder } from '../../../../common/components/ImagePlaceholder';
import { setIsMediaFetched, setMedia } from '../../../../redux/slice/campaignSlice';
import { RootState } from '../../../../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import EditIcon from '../../../../constants/icons/EditIcon';
import styled from '@emotion/styled';
import { COLOR_MAP } from '../../../../constants/ColorNames';
import { useGetCompanyMediaQuery } from '../../../../redux/services/folderName';



const PreviewWrapper = styled.div`
  position: relative;
  border-radius: 4px;
  height:36px;
  box-sizing: border-box;
`


const EditWrapper = styled.div`
  position: absolute;
  bottom: 2px;
  cursor: pointer;
  right: 2px;
  border-radius: 4px;
  max-height: 14px;
  padding: 1px;
`;
const InfoText = styled.p`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 400;
  line-height: 2rem;
  color: ${COLOR_MAP.GREY_1};
`

type ImagePreviewProps = {
  src: string,
  alt: string,
  handleEdit: () => void,
  isEditable: boolean,
  isBanner?: boolean
}

type MediaListType = {
  cover: FileList | string | null,
  logo: FileList | string | null
}

const initialMediaState: MediaListType = {
  cover: null,
  logo: null,
};

type Type = 'cover' | 'logo';

const ImagePreview = (props: ImagePreviewProps) => {
  const { src, alt, handleEdit, isEditable, isBanner } = props;
  return (
    <PreviewWrapper
      style={{
        padding: isBanner ? "0px" : "4px",
        border: isBanner ? "none" : `1px solid ${COLOR_MAP.GREY}`,
      }}
    >
      <Box
        sx={{
          height: isBanner ? "100%" : "26px",
          width: isBanner ? "100%" : "26px",
        }}
      >
        <img
          src={src}
          alt={alt}
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "4px",
          }}
        />
      </Box>
      {isEditable ? (
        <EditWrapper
          style={{ ...(isBanner && { backgroundColor: COLOR_MAP.WHITE }) }}
        >
          <EditIcon onClick={handleEdit} pathFill={COLOR_MAP.GREY_2} />
        </EditWrapper>
      ) : null}
    </PreviewWrapper>
  );
};

const CompanyMedia = () => {
  const [mediaList, setMediaList] = React.useState<MediaListType>(initialMediaState);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [type, setType] = React.useState<Type>('logo');
  const media = useSelector((state: RootState) => state.campaign.mediaList);
  const isMediaAllowed = useSelector((state: RootState) => state.campaign.isMediaUploadAllowed);
  const [errorMessage, setErrorMessage] = React.useState<string>('')
  const dispatch = useDispatch();
  const [selectedMedia, setSelectedMedia] =React.useState<MediaListType>(initialMediaState);

  const { data: companyMedia, isSuccess } = useGetCompanyMediaQuery(1);

  React.useEffect(() => {
    if (!companyMedia) return;
    if(companyMedia.base64Logo || companyMedia.base64Banner){
      let logo = companyMedia.base64Logo ? `data:image/jpeg;base64,${companyMedia.base64Logo}` : '';
      let cover = companyMedia.base64Banner ? `data:image/jpeg;base64,${companyMedia.base64Banner}` : '';
      setMediaList({ ...mediaList, logo: logo, cover: cover});
      setSelectedMedia({ ...mediaList, logo: logo, cover: cover });
      dispatch(setMedia({ mediaList: { logo: logo, cover: cover } })) 
      
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyMedia, dispatch])

  useEffect(()=>{
    if(isSuccess){
      dispatch(setIsMediaFetched({isMediaFetched: true}));
    }
  },[dispatch, isSuccess])

  const handleMediaUpload = (fileList: FileList) => {
    if (!fileList || !fileList.length) return;
    const isValidFileSize = checkMediaSize(fileList);
    if (!isValidFileSize) {
      setErrorMessage('File size cannot be more than 1mb');
      return;
    }
    if (isValidFileSize && errorMessage === 'File size cannot be more than 1mb') {
      setErrorMessage('');
    }

    // Define the expected dimensions (width and height)
    const expectedWidth = type === 'logo' ? 160 : 632;
    const expectedHeight = type === 'logo' ? 60 : 150;

    Array.from(fileList).some((file) => {
      return new Promise((resolve) => {
        const image = new Image();
        image.src = URL.createObjectURL(file);
        image.onload = () => {
          const width = image.width;
          const height = image.height;
          if (width < expectedWidth || height < expectedHeight) {
            resolve(true); // Image has invalid dimensions
            setErrorMessage(`Choose the appropriate file size and dimension`);
          } else {
            resolve(false); // Image has valid dimensions
            if (errorMessage === 'Choose the appropriate file size and dimension') {
              setErrorMessage('');
            }
          }
        };
      });
    });

    const newFileListArray = fileList;
    if (type === 'cover') {
      setMediaList({ ...mediaList, cover: newFileListArray });
    }
    else if (type === 'logo') {
      setMediaList({ ...mediaList, logo: newFileListArray });
    }

  }

  const handleUpdateMedia = async (src: string) => {

    // let newFileListArray =  mediaList[type];
    if (errorMessage) return;
    let updatedMedia = {...selectedMedia};
    if (src) {
      // let mediaBase64String =  await getBase64(newFileListArray[0]);
      if (type === 'cover') {
       updatedMedia['cover'] = mediaList.cover;
       setSelectedMedia(updatedMedia);
        dispatch(setMedia({ mediaList: { cover: src } }))
      }
      else {
        updatedMedia['logo'] = mediaList.logo;
        setSelectedMedia(updatedMedia);
        dispatch(setMedia({ mediaList: { logo: src } }))
      }
    }
  }

  const handlePlaceholderClick = React.useCallback((type: Type) => {
    setType(type);
    setIsModalOpen(true);
  }, []);

  const handleModalClose = React.useCallback((isEdited: boolean) => {
    if(isEdited){
      setIsModalOpen(false)
      return;
    }
    let updatedMedia = {...mediaList}
    if(!media[type]){
      updatedMedia[type] = null;
      setMediaList(updatedMedia);
    }

    if(selectedMedia[type] !== mediaList[type]){
      setMediaList({ ...mediaList, [type]: selectedMedia[type] });
    }
    if(errorMessage){
      setErrorMessage('');
    }
    setIsModalOpen(false)
  },[errorMessage, media, mediaList, selectedMedia, type])



  return (
    <>
      <Grid container gap={"8px"}>
        <Grid
          xs={4.5}
          item
          sx={{ backgroundColor: COLOR_MAP.GREY_9, borderRadius: "8px", p:'8px'}}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "end",
              width: "100%",
              mb:'8px',
              flexWrap: "wrap"
            }}
          >
            <Typography sx={{ fontSize: "12px", fontWeight: 500, whiteSpace: "nowrap" }}>
              Logo
            </Typography>
            <Typography
              sx={{ fontSize: "10px", fontWeight: 400, color: "#777585", whiteSpace: "nowrap" }}
            >
              (160x160 px)
            </Typography>
          </Box>
          {media.logo ? (
            <ImagePreview
              isEditable={isMediaAllowed.logo}
              src={media.logo}
              alt="logo"
              handleEdit={() => handlePlaceholderClick("logo")}
            />
          ) : isMediaAllowed.logo ? (
            <ImagePlaceholder
              label={"Upload"}
              onClick={() => handlePlaceholderClick("logo")}
            />
          ) : null}
        </Grid>
        <Grid
          xs={7}
          item
          sx={{ backgroundColor: COLOR_MAP.GREY_9, borderRadius: "8px", p:'8px' }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "end",
              width: "100%",
              mb:'8px',
              flexWrap: "wrap"
            }}
          >
            <Typography sx={{ fontSize: "12px", fontWeight: 500, whiteSpace: "nowrap"}}>
              Banner
            </Typography>
            <Typography
              sx={{ fontSize: "10px", fontWeight: 400, color: "#777585", whiteSpace: "nowrap"}}
            >
              (648x150 px)
            </Typography>
          </Box>
          {media.cover ? (
            <ImagePreview
              isEditable={isMediaAllowed.cover}
              src={media.cover}
              alt="cover"
              handleEdit={() => handlePlaceholderClick("cover")}
              isBanner={true}
            />
          ) : isMediaAllowed.cover ? (
            <ImagePlaceholder
              label={"Upload"}
              onClick={() => handlePlaceholderClick("cover")}
            />
          ) : null}
        </Grid>
        {/* {(isMediaAllowed.cover || isMediaAllowed.logo) && <InfoText>Size: logo - 160 X 60px & header image - 648 X 150 px</InfoText>} */}
      </Grid>
      {isModalOpen && (
        <CompanyMediaModal
          type={type}
          errorMessage={errorMessage}
          handleMediaUpload={handleMediaUpload}
          handleClose={handleModalClose}
          mediaFile={mediaList[type]}
          uploadedMedia={media[type]}
          handleUpdateMedia={handleUpdateMedia}
        />
      )}
    </>
  );
}

export default CompanyMedia;
